import * as React from "react";
import { useMemo } from "react";
import { Typography, Grid, Box, useMediaQuery, useTheme } from "@mui/material";
import { IMAGE_BASE_PATH } from "../../config/paths";
import { useTranslation } from "react-i18next";
import { useSeo } from "../../components/seo/useSeo";
import { useJsonLd } from "../../components/seo/useJsonLd";
import { SITE_URL } from "../../config/site";

/* =======================================================================================
 * A11y/SEO helper
 * ===================================================================================== */
const visuallyHiddenSx = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: 0,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

/* =======================================================================================
 * Layout helpers
 * ===================================================================================== */

/** Mesmas dimensões para Basalt / Lightweight / Greenpoxy */
const IMAGE_FRAME_MAX_WIDTH = 500;
const IMAGE_FRAME_ASPECT = "16 / 11";

/** Limita o bloco de texto para casar com o frame */
const TEXT_MAX_WIDTH = IMAGE_FRAME_MAX_WIDTH;

type ImageFrameProps = {
  src: string;
  alt: string;
  caption?: string;
  overlay?: React.ReactNode;
  priority?: boolean;
};

function ImageFrame({ src, alt, caption, overlay, priority }: ImageFrameProps) {
  return (
    <Box
      component="figure"
      sx={{
        m: 0,
        position: "relative",
        width: "100%",
        maxWidth: IMAGE_FRAME_MAX_WIDTH,
        aspectRatio: IMAGE_FRAME_ASPECT,
        overflow: "hidden",
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority ? "high" : "auto"}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />

      {overlay}

      {caption ? (
        <Box component="figcaption" sx={visuallyHiddenSx}>
          {caption}
        </Box>
      ) : null}
    </Box>
  );
}

type ZigZagRowProps = {
  reverseOnDesktop?: boolean;
  text: React.ReactNode;
  media: React.ReactNode;
  ariaLabelledBy?: string;
};

function ZigZagRow({
  reverseOnDesktop,
  text,
  media,
  ariaLabelledBy,
}: ZigZagRowProps) {
  return (
    <Box
      component="section"
      aria-labelledby={ariaLabelledBy}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: {
          xs: "column", // ✅ mobile: sempre texto em cima, imagem embaixo
          md: reverseOnDesktop ? "row-reverse" : "row", // ✅ desktop: zig-zag visual
        },
        alignItems: "center",
        justifyContent: "center",
        gap: { xs: 2.5, md: 4 },
      }}
    >
      {/* TEXTO sempre primeiro no DOM (SEO) */}
      <Box
        sx={{
          width: "100%",
          flex: "1 1 0",
          display: "flex",
          justifyContent: {
            xs: "center",
            md: reverseOnDesktop ? "flex-start" : "flex-end",
          },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: TEXT_MAX_WIDTH }}>{text}</Box>
      </Box>

      {/* IMAGEM */}
      <Box
        sx={{
          width: "100%",
          flex: "1 1 0",
          display: "flex",
          justifyContent: {
            xs: "center",
            md: reverseOnDesktop ? "flex-end" : "flex-start",
          },
        }}
      >
        {media}
      </Box>
    </Box>
  );
}

/* =======================================================================================
 * Page
 * ===================================================================================== */
export default function Sustentabilidade() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ✅ locale p/ OG (pt_BR / en_US / es_ES)
  const ogLocale = useMemo(() => {
    const raw = (i18n.resolvedLanguage || i18n.language || "pt").toLowerCase();
    const p = raw.split("-")[0];
    if (p === "en") return "en_US";
    if (p === "es") return "es_ES";
    return "pt_BR";
  }, [i18n.resolvedLanguage, i18n.language]);

  const mainTitleId = "sustentabilidade-title";
  const basaltTitleId = "sustentabilidade-basalt-title";
  const woodTitleId = "sustentabilidade-wood-title";
  const greenpoxyTitleId = "sustentabilidade-greenpoxy-title";

  const bannerSrc = isMobile
    ? `${IMAGE_BASE_PATH}sustentabilidade/SustentabilidadeBannerMobile.avif`
    : `${IMAGE_BASE_PATH}sustentabilidade/SustentabilidadeBanner.avif`;

  // ✅ SEO: canonical + og:image absolute (se você tiver domínio, coloque aqui)
  const canonical = `${SITE_URL}/sustentabilidade`;

  const ogImage = `${SITE_URL}${bannerSrc}`;

  // ✅ useSeo (title/description devem vir do i18n)
  useSeo({
    title: t("Sustentabilidade.seoTitle"),
    description: t("Sustentabilidade.seoDescription"),
    canonical,
    image: ogImage,
    type: "website",
    noindex: false,
    locale: ogLocale,
  });

  // ✅ JSON-LD: WebPage + Breadcrumb
  const jsonLd = useMemo(() => {
    const pageName = t("Sustentabilidade.seoTitle");
    const pageDesc = t("Sustentabilidade.seoDescription");

    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": canonical,
          url: canonical,
          name: pageName,
          description: pageDesc,
          inLanguage: ogLocale.replace("_", "-"),
          primaryImageOfPage: {
            "@type": "ImageObject",
            url: ogImage,
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: t("Breadcrumb.home"),
              item: (() => {
                try {
                  const u = new URL(canonical);
                  return u.origin + "/";
                } catch {
                  return "/";
                }
              })(),
            },
            {
              "@type": "ListItem",
              position: 2,
              name: t("Sustentabilidade.titulo"),
              item: canonical,
            },
          ],
        },
      ],
    };
  }, [canonical, ogImage, ogLocale, t]);

  useJsonLd("sustentabilidade", jsonLd);

  const h2Sx = useMemo(
    () =>
      ({
        fontFamily: "Oswald, sans-serif",
        fontWeight: 600,
        fontSize: "1.3rem",
        letterSpacing: "-0.03em",
        textTransform: "uppercase",
        color: "black",
        textAlign: isSmallScreen ? "center" : "left",
      }) as const,
    [isSmallScreen]
  );

  const h3Sx = useMemo(
    () =>
      ({
        fontFamily: "Oswald, sans-serif",
        fontWeight: 600,
        fontSize: { xs: "1.7rem", md: "2rem" },
        letterSpacing: "-0.03em",
        textTransform: "uppercase",
        color: "black",
        textAlign: isSmallScreen ? "center" : "left",
      }) as const,
    [isSmallScreen]
  );

  const pSx = useMemo(
    () =>
      ({
        fontFamily: "Helvetica,Arial,Verdana,sans-serif",
        fontWeight: 400,
        lineHeight: 1.6,
        letterSpacing: "0.02em",
        fontSize: "0.9rem",
        color: "black",
        textAlign: isSmallScreen ? "center" : "left",
        mt: 3,
      }) as const,
    [isSmallScreen]
  );

  return (
    <Grid
      component="main"
      container
      spacing={4}
      justifyContent="center"
      sx={{ backgroundColor: "white" }}
      aria-labelledby={mainTitleId}
    >
      {/* H1 SEO (invisível, único) */}
      <Typography component="h1" id={mainTitleId} sx={visuallyHiddenSx}>
        {t("Sustentabilidade.titulo")}
      </Typography>

      {/* Banner */}
      <Grid size={12} sx={{ height: "80vh" }}>
        <Box sx={{ position: "relative", height: "100%", width: "100%", overflow: "hidden" }}>
          <Box
            component="img"
            src={bannerSrc}
            alt={t("Sustentabilidade.bannerAlt")}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        </Box>
      </Grid>

      {/* Conteúdo */}
      <Grid
        size={12}
        component="section"
        aria-label={t("Sustentabilidade.conteudoAriaLabel")}
        sx={{ px: 2, pb: 6 }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1200,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 6, md: 8 },
          }}
        >
          {/* ===================== Basalt ===================== */}
          <ZigZagRow
            reverseOnDesktop
            ariaLabelledBy={basaltTitleId}
            text={
              <Box sx={{ textTransform: "none" }}>
                <Typography component="h2" id={basaltTitleId} sx={h2Sx}>
                  {t("Sustentabilidade.basaltoTitulo1")}
                </Typography>

                <Typography
                  component="h3"
                  sx={{ ...h3Sx, fontSize: { xs: "1.7rem", md: "1.7rem" } }}
                >
                  {t("Sustentabilidade.basaltoTitulo2")}
                  {isSmallScreen ? " " + t("Sustentabilidade.basaltoTitulo3") : ""}
                </Typography>

                {!isSmallScreen && (
                  <Typography component="h3" sx={{ ...h3Sx, fontSize: "1.7rem" }}>
                    {t("Sustentabilidade.basaltoTitulo3")}
                  </Typography>
                )}

                <Typography component="p" sx={pSx}>
                  {t("Sustentabilidade.basaltoDescricao")}
                </Typography>
              </Box>
            }
            media={
              <ImageFrame
                src={`${IMAGE_BASE_PATH}sustentabilidade/Basalt.avif`}
                alt={t("Sustentabilidade.basaltoImgAlt")}
                caption={t("Sustentabilidade.basaltoImgCaption")}
                overlay={
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <Box
                      component="img"
                      src={`${IMAGE_BASE_PATH}sustentabilidade/BSLT.avif`}
                      alt=""
                      aria-hidden="true"
                      sx={{
                        width: { xs: "50%", sm: "40%", md: "30%", lg: "30%", xl: "25%" },
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                }
              />
            }
          />

          {/* ===================== Paulownia + Lightweight core ===================== */}
          <ZigZagRow
            reverseOnDesktop={false}
            ariaLabelledBy={woodTitleId}
            text={
              <Box sx={{ textTransform: "none" }}>
                <Typography component="h2" id={woodTitleId} sx={h2Sx}>
                  PAULOWNIA, {t("Sustentabilidade.paulowniaTitulo1")}
                </Typography>

                <Typography component="h3" sx={h3Sx}>
                  {t("Sustentabilidade.paulowniaTitulo2")}
                  {isSmallScreen ? " " + t("Sustentabilidade.paulowniaTitulo3") : ""}
                </Typography>

                {!isSmallScreen && (
                  <Typography component="h3" sx={h3Sx}>
                    {t("Sustentabilidade.paulowniaTitulo3")}
                  </Typography>
                )}

                <Typography component="p" sx={pSx}>
                  {t("Sustentabilidade.paulowniaDescricao")}
                </Typography>
              </Box>
            }
            media={
              <ImageFrame
                src={`${IMAGE_BASE_PATH}sustentabilidade/LIGHTWEIGHTWOODCORE.avif`}
                alt={t("Sustentabilidade.paulowniaImgAlt")}
                caption={t("Sustentabilidade.paulowniaImgCaption")}
              />
            }
          />

          {/* ===================== Greenpoxy ===================== */}
          <ZigZagRow
            reverseOnDesktop
            ariaLabelledBy={greenpoxyTitleId}
            text={
              <Box sx={{ textTransform: "none" }}>
                <Typography component="h2" id={greenpoxyTitleId} sx={h2Sx}>
                  {t("Sustentabilidade.greenpoxyTitulo1")}
                </Typography>

                <Typography component="h3" sx={h3Sx}>
                  {t("Sustentabilidade.greenpoxyTitulo2")}
                </Typography>

                <Typography component="p" sx={{ ...pSx, fontFamily: "Roboto, sans-serif" }}>
                  {t("Sustentabilidade.greenpoxyDescricao")}
                </Typography>
              </Box>
            }
            media={
              <ImageFrame
                src={`${IMAGE_BASE_PATH}sustentabilidade/greenpoxy_gradient.avif`}
                alt={t("Sustentabilidade.greenpoxyImgAlt")}
                caption={t("Sustentabilidade.greenpoxyImgCaption")}
                overlay={
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "15%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      textAlign: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <Box
                      component="img"
                      src={`${IMAGE_BASE_PATH}sustentabilidade/GreenPoxy.avif`}
                      alt=""
                      aria-hidden="true"
                      sx={{ width: "55%", objectFit: "cover", display: "block", mx: "auto" }}
                    />
                  </Box>
                }
              />
            }
          />
        </Box>
      </Grid>
    </Grid>
  );
}
